/**
 * Currency Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help with currency formatting
 *
 * Current contents
 * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
 *
 */

import { type CurrencyCode } from "@shopify/hydrogen-react/storefront-api-types";
import {
  getShopifyCountry,
  getShopifyLocale,
} from "@root/tablescape/utilities/markets";

// Memoized fr-FR detection to avoid repeated lookups
let cachedIsFrFr: boolean | undefined;
let lastCountryForFrCheck: ReturnType<typeof getShopifyCountry> | undefined;
let lastLocaleForFrCheck: ReturnType<typeof getShopifyLocale> | undefined;

export function getIsFrenchCountryAndFrenchLocale(): boolean {
  const country = getShopifyCountry();
  const locale = getShopifyLocale();
  if (
    cachedIsFrFr !== undefined &&
    country === lastCountryForFrCheck &&
    locale === lastLocaleForFrCheck
  ) {
    return cachedIsFrFr;
  }
  const result = country === "FR" && locale?.toLowerCase() === "fr";
  lastCountryForFrCheck = country;
  lastLocaleForFrCheck = locale;
  cachedIsFrFr = result;
  return result;
}

/**
 * Format money values based on your shop currency settings
 * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
 * or 3.00 dollars
 * @param  {String} format - shop money_format setting
 * @return {String} value - formatted value
 */

export type Money = string | number;

export function formatMoney(
  cents: Money,
  // eslint-disable-next-line no-template-curly-in-string
  format = "${{ amount }}",
  currencySuffix: CurrencyCode | false | undefined = undefined
): string {
  if (typeof cents === "string") {
    // Handle string input - convert to number, supporting both "1234" and "12.34" formats
    const numericValue = parseFloat(cents);
    if (isNaN(numericValue)) {
      console.warn(`Invalid currency string format: ${cents}`);
      cents = 0;
    } else {
      // If the string contains a decimal point, treat as dollars and convert to cents
      // Otherwise, treat as cents directly
      cents = cents.includes(".")
        ? Math.round(numericValue * 100)
        : Math.round(numericValue);
    }
  }

  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  const isFrenchCountryAndFrenchLocale = getIsFrenchCountryAndFrenchLocale();

  function formatWithDelimiters(
    number: number,
    precision: number,
    thousands?: string,
    decimal?: string
  ): string {
    precision = precision !== undefined ? precision : 2;
    thousands = thousands || ",";
    decimal = decimal || ".";

    if (isNaN(number) || number === null || number === undefined) {
      return "0";
    }

    const stringNumber = (number / 100.0).toFixed(precision);

    const parts = stringNumber.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1" + thousands
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";

    if (precision === 0) {
      return dollarsAmount;
    } else {
      return dollarsAmount + centsAmount;
    }
  }

  const thousandsSep = isFrenchCountryAndFrenchLocale ? "." : ",";
  const decimalSep = isFrenchCountryAndFrenchLocale ? "," : ".";

  const token = format.match(placeholderRegex)?.[1];
  const selectedToken = ((): string => {
    switch (token) {
      case "amount":
      case "amount_no_decimals":
      case "amount_with_space_separator":
      case "amount_no_decimals_with_comma_separator":
      case "amount_no_decimals_with_space_separator":
      case "amount_no_trailing_zeroes":
        return token;
      default:
        return "amount"; // fallback for unknown/malformed tokens
    }
  })();

  switch (selectedToken) {
    case "amount":
      value = formatWithDelimiters(cents, 2, thousandsSep, decimalSep);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0, thousandsSep, decimalSep);
      break;
    case "amount_with_space_separator":
      // Use locale-appropriate decimal separator for space separator format
      value = formatWithDelimiters(cents, 2, " ", decimalSep);
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ",", decimalSep);
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_trailing_zeroes":
      {
        const formatted = formatWithDelimiters(
          cents,
          2,
          thousandsSep,
          decimalSep
        );
        value = formatted.replace(`${decimalSep}00`, "");
      }
      break;
  }

  if (currencySuffix) {
    value = `${value} ${currencySuffix}`;
  }

  const hasPlaceholder = placeholderRegex.test(format);
  const formattedValue = hasPlaceholder
    ? format.replace(placeholderRegex, value)
    : value;

  // Get currency symbol once to avoid duplicate calls
  const symbol = currencySymbol();

  if (isFrenchCountryAndFrenchLocale) {
    // Preserve format structure but ensure trailing symbol with a space for fr-FR
    const withoutLeadingSymbol = formattedValue.replace(/^[€$£%]/, "").trim();
    return `${withoutLeadingSymbol} ${symbol}`;
  }

  let output = formattedValue.replace(/^[$%]/, symbol);
  // If no symbol present at start or end, prefix it
  const hasAnySymbol = /(^[€£$])|([€£$]\s?$)/.test(output);
  if (!hasAnySymbol) {
    output = `${symbol}${output}`;
  }
  return output;
}

export function currencySymbol(): string {
  if (typeof window === "undefined") return "$";
  if (window.Shopify && window.Shopify?.currency?.active === "GBP") {
    return "£";
  } else if (window.Shopify && window.Shopify?.currency?.active === "EUR") {
    return "€";
  } else {
    return "$";
  }
}
